import { type Dispatch, type SetStateAction } from "react";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Alert, AlertTitle, Box, Button, Link, Stack, TextField, Typography } from "@mui/material";

import { useTestConnection } from "../hooks";
import { type FormState } from "../types";

type Props = {
  formState: FormState;
  onChange: Dispatch<SetStateAction<FormState>>;
};

export default function ApiKeyStep({ formState, onChange }: Props) {
  const { testConnection } = useTestConnection();

  const handleChange = (field: "apiKey", value: string) => {
    onChange({
      ...formState,
      [field]: value,
      connectionSuccess: false,
      connectionMessage: "",
    });
  };

  const handleTestConnection = async () => {
    if (!formState.apiKey) {
      return;
    }

    onChange((prevState) => ({
      ...prevState,
      loading: true,
      connectionSuccess: false,
      connectionMessage: "",
    }));

    const testResult = await testConnection(formState);

    onChange((prevState) => ({
      ...prevState,
      loading: false,
      connectionSuccess: testResult.success,
      connectionMessage: testResult.message,
    }));
  };

  const isTestDisabled = !formState.apiKey || formState.loading;

  return (
    <Stack data-cy="api-key-step" spacing={4}>
      <Typography variant="h3">API Key</Typography>

      <Stack spacing={2}>
        <Typography variant="body1">
          1. Sign in to{" "}
          <Link
            href="https://app.cloudzero.com"
            target="_blank"
            data-cy="cloudzero-login-link"
            display="inline-flex"
            underline="none"
            sx={{ alignItems: "center", gap: 0.5 }}
          >
            CloudZero
            <OpenInNewIcon fontSize="inherit" />
          </Link>
        </Typography>
        <Typography variant="body1">
          2. Navigate to SETTINGS, and then to API KEYS, and select CREATE NEW API KEY
        </Typography>
        <Typography variant="body1">
          3. Enter a name for the key (ie. "doit service account"), and (optional) add a description
        </Typography>
        <Typography variant="body1">
          4. Select the following scopes
          <Box pl={3}>
            {formState.fetchCosts && (
              <>
                <Typography variant="body1">a. billing:read_costs</Typography>
                <Typography variant="body1">b. billing:read_dimensions</Typography>
                <Typography variant="body1">c. budget:read_budgets</Typography>
                <Typography variant="body1">d. budget:read_budget</Typography>
              </>
            )}
            {formState.fetchInsights && (
              <Typography variant="body1">{formState.fetchCosts ? "e" : "a"}. insights:read_insights</Typography>
            )}
          </Box>
        </Typography>
        <Typography variant="body1">5. Select CREATE API KEY, paste the created API key below</Typography>
      </Stack>

      <TextField
        label="API Key"
        fullWidth
        value={formState.apiKey}
        required
        onChange={(event) => {
          handleChange("apiKey", event.target.value);
        }}
        placeholder="Enter your CloudZero API Key"
        data-cy="api-key-input"
      />

      <Stack spacing={2}>
        <Typography variant="body1">
          To make sure the setup is successful, we must test the connection. This should take a couple of seconds.
        </Typography>
        <Button
          variant="outlined"
          sx={{ width: "140px" }}
          onClick={handleTestConnection}
          disabled={isTestDisabled}
          loading={formState.loading}
          data-cy="test-connection-button"
        >
          Test connection
        </Button>

        {formState.connectionMessage && !formState.connectionSuccess && (
          <Alert severity="error" data-cy="connection-test-error">
            <Stack>
              <AlertTitle>Test failed</AlertTitle>
              {formState.connectionMessage}
            </Stack>
          </Alert>
        )}

        {formState.connectionMessage && formState.connectionSuccess && (
          <Alert severity="success" data-cy="connection-test-success">
            <AlertTitle>Test successful</AlertTitle>
            <Typography variant="body1">{formState.connectionMessage}</Typography>
          </Alert>
        )}
      </Stack>
    </Stack>
  );
}
